import { Component, computed, effect, input, output, signal } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { AddressVerificationChoice, AddressVerificationOutcome } from '../../core/enums/base.enums';
import { AddressDeliverability } from '../../core/enums/data.enums';
import { AddressVerificationPrompt, AddressVerificationSide } from '../../core/models/ui/address-verification-prompt.model';
import { AddressVerificationMessagingConstants } from '../../core/constants/messaging.constants';
import { AddressUtilities } from '../../core/utilities/address.utilities';
import { EnumUtilities } from '../../core/utilities/enum.utilities';
import { EscCloseDirective } from '../../core/directives/esc-close.directive';

@Component({
  selector: 'ms-address-verification-modal',
  imports: [FaDuotoneIconComponent, EscCloseDirective],
  templateUrl: './address-verification-modal.component.html',
  styleUrl: './address-verification-modal.component.scss'
})
export class AddressVerificationModalComponent {
  public modalId = input<string>('addressVerificationModal');
  public prompt = input<AddressVerificationPrompt | undefined>(undefined);

  /** Save the selected address. Standardized yields Verified (with a geocode where there is one); Original yields UserEntered. */
  public confirmed = output<AddressVerificationChoice>();
  /** Cancel: back to the form with the entry untouched. Submitting again is a new verification. */
  public editRequested = output<void>();

  public AddressVerificationChoice = AddressVerificationChoice;
  public Messages = AddressVerificationMessagingConstants;

  // The provider's version is preselected: it is the side that yields a Verified address, and on the
  // most common correction — an added ZIP+4 — it is what the user meant to type anyway.
  public selectedChoice = signal<AddressVerificationChoice>(AddressVerificationChoice.Standardized);

  constructor() {
    // Every new verification starts from the default rather than inheriting the previous answer,
    // which would otherwise persist across submits because the modal is never destroyed.
    effect(() => {
      this.prompt();
      this.selectedChoice.set(AddressVerificationChoice.Standardized);
    });
  }

  public outcome = computed<AddressVerificationOutcome>(() => {
    const verification = this.prompt()?.verification;
    if (!verification) {
      return AddressVerificationOutcome.Corrected;
    }

    if (verification.deliverability === AddressDeliverability.Undeliverable) {
      return AddressVerificationOutcome.Undeliverable;
    }
    if (verification.deliverability === AddressDeliverability.DeliverableWithIssues) {
      return AddressVerificationOutcome.DeliverableWithIssues;
    }
    if (AddressUtilities.isZipPlus4OnlyCorrection(verification.originalAddress, verification.standardizedAddress)) {
      return AddressVerificationOutcome.ZipExtensionAdded;
    }

    return AddressVerificationOutcome.Corrected;
  });

  public originalLines = computed<string[]>(() => this.linesFor(this.prompt()?.original));
  public standardizedLines = computed<string[]>(() => this.linesFor(this.prompt()?.standardized));

  /**
   * Whether the two sides differ anywhere the user can see.
   *
   * `isCorrected` is the API's field-by-field comparison, so it also flags differences this dialog
   * never draws — a null against an empty second line, a stray trailing space. Those put two
   * identical cards on screen under a message about a difference that was found (DEV-2783).
   */
  public hasVisibleDifference = computed<boolean>(() =>
    !AddressUtilities.isSameRenderedAddress(this.originalLines(), this.standardizedLines()));

  /**
   * Whether the provider returned a correction this dialog is willing to offer.
   *
   * False whenever `isCorrected` is false, because the API sets that flag by comparing every field —
   * so the two sides are identical and a choice would be between one address and itself. Also false
   * for an Undeliverable verdict: the provider may still return a tidied address, but both sides save
   * as UserEntered with no geocode, so presenting it as a correction would claim something untrue.
   */
  private isOfferableCorrection = computed<boolean>(() =>
    !!this.prompt()?.verification.isCorrected && this.outcome() !== AddressVerificationOutcome.Undeliverable);

  /** Whether there are two addresses to pick between — an offered correction that is visible. */
  public hasChoice = computed<boolean>(() => this.isOfferableCorrection() && this.hasVisibleDifference());

  // A reported deliverability problem is a nudge toward fixing the address rather than saving it,
  // so Edit carries the primary styling on those two outcomes and the save carries it otherwise.
  public editIsPrimaryAction = computed<boolean>(() =>
    this.outcome() === AddressVerificationOutcome.DeliverableWithIssues
    || this.outcome() === AddressVerificationOutcome.Undeliverable);

  public title = computed<string>(() => {
    switch (this.outcome()) {
      case AddressVerificationOutcome.Undeliverable:
        return AddressVerificationMessagingConstants.TITLE_UNDELIVERABLE();
      case AddressVerificationOutcome.DeliverableWithIssues:
        return AddressVerificationMessagingConstants.TITLE_ISSUES();
      default:
        return AddressVerificationMessagingConstants.TITLE_CORRECTION();
    }
  });

  public promptText = computed<string>(() => {
    switch (this.outcome()) {
      case AddressVerificationOutcome.Undeliverable:
        return AddressVerificationMessagingConstants.PROMPT_UNDELIVERABLE();
      case AddressVerificationOutcome.DeliverableWithIssues:
        return AddressVerificationMessagingConstants.PROMPT_ISSUES();
      case AddressVerificationOutcome.ZipExtensionAdded:
        return AddressVerificationMessagingConstants.PROMPT_ZIP_EXTENSION();
      default:
        // One address on screen has no "different version" to choose from, whether the provider
        // returned nothing to correct or corrected only what the dialog does not draw.
        return this.hasChoice()
          ? AddressVerificationMessagingConstants.PROMPT_CORRECTION()
          : AddressVerificationMessagingConstants.PROMPT_NO_DIFFERENCE();
    }
  });

  /**
   * Whether the user has their own entry selected over the verified one.
   *
   * Persistent rather than a prompt on confirm: the warning shows the moment that selection is made
   * and stays while it stands, so the risk is visible at the point of choosing. The warning keeps
   * its space in the layout either way (hidden, not removed), so toggling between the two cards
   * never resizes the dialog under the user's cursor. Only reachable where there is something to
   * choose between — the problem outcomes already explain themselves in the prompt above.
   */
  public showUnverifiedWarning = computed<boolean>(() =>
    this.hasChoice() && this.selectedChoice() === AddressVerificationChoice.Original);

  // With nothing to pick between, the address on screen is the one the user typed — the two sides
  // are identical, or the provider offered no alternative worth accepting.
  public singleSideLines = computed<string[]>(() => this.originalLines());

  // The icon and the alert tint both follow the verdict, so the banner reads as the status before a
  // word of it is parsed. The icon takes its colour from the alert rather than carrying its own.
  public icon = computed<string>(() => {
    switch (this.outcome()) {
      case AddressVerificationOutcome.Undeliverable:
        return 'circle-xmark';
      case AddressVerificationOutcome.DeliverableWithIssues:
        return 'circle-exclamation';
      default:
        return 'circle-check';
    }
  });

  public alertClass = computed<string>(() => {
    switch (this.outcome()) {
      case AddressVerificationOutcome.Undeliverable:
        return 'alert-danger';
      case AddressVerificationOutcome.DeliverableWithIssues:
        return 'alert-warning';
      default:
        return 'alert-success';
    }
  });

  /**
   * The verdict and the provider's own account of it, shown once for the dialog rather than against
   * either card.
   *
   * It describes the lookup, not a particular rendering of the address, and standardizing an
   * address is not the same as fixing it: asked about a building with no unit, the provider may
   * return a standardized address with a unit of its own choosing, which is not the resident's.
   * Badging one card would tell the user the other is the safe one — so neither is badged, and the
   * prompt says plainly that only editing the address resolves the problem.
   *
   * The description is the API's own wording for `providerDeliverability`, used verbatim: the
   * mapping from `deliverable_missing_unit` and friends lives there, and duplicating it here would
   * be two copies to drift apart.
   */
  public deliverabilityLabel = computed<string>(() =>
    EnumUtilities.convertToLabel(this.prompt()?.verification.deliverability));

  public deliverabilityDescription = computed<string | undefined>(() =>
    this.prompt()?.verification.providerDeliverabilityDescription);

  /** One radio group per modal instance, so two of these on a page cannot share a selection. */
  public radioGroupName = computed<string>(() => `${this.modalId()}-address-choice`);

  // Derived from the modal id so the references resolve and stay unique when more than one of these
  // is rendered. Bootstrap supplies role="dialog" and aria-modal on show, but not the labelling.
  public titleId = computed<string>(() => `${this.modalId()}-title`);
  public promptId = computed<string>(() => `${this.modalId()}-prompt`);

  public select(choice: AddressVerificationChoice): void {
    this.selectedChoice.set(choice);
  }

  public onConfirm(): void {
    if (this.hasChoice()) {
      this.confirmed.emit(this.selectedChoice());
      return;
    }

    // Withholding the choice must not change what saves: a correction hidden by an identical
    // rendering is still one the provider stands behind, so it saves Verified exactly as the
    // preselected radio would have. Everywhere else here, what was entered is what saves.
    this.confirmed.emit(this.isOfferableCorrection()
      ? AddressVerificationChoice.Standardized
      : AddressVerificationChoice.Original);
  }

  private linesFor(side: AddressVerificationSide | undefined): string[] {
    return side ? AddressUtilities.formatVerifiedLines(side) : [];
  }
}
